import {
  PENDING_CHUNK_RELOAD_KEY,
  PendingChunkReload,
  STALE_MODULE_RELOAD_KEY,
} from 'shared/chunk-reload';

/**
 * We reload a given page at most once per tab: a reload records the page's
 * URL, and the same URL failing again is reported instead of reloaded. However
 * long a failure takes, a page that keeps failing never reloads in a loop,
 * while a later failure on another page (e.g. after the next deploy) can
 * still recover with a reload.
 */
export function shouldReloadForStaleModule(
  lastReloadUrl: string | null | undefined,
  currentUrl: string,
) {
  return lastReloadUrl !== currentUrl;
}

function readLastReloadUrl(): string | null | undefined {
  try {
    return sessionStorage.getItem(STALE_MODULE_RELOAD_KEY);
  } catch {
    return;
  }
}

function recordAttempt(pending: PendingChunkReload): boolean {
  try {
    sessionStorage.setItem(STALE_MODULE_RELOAD_KEY, pending.url);
    sessionStorage.setItem(PENDING_CHUNK_RELOAD_KEY, JSON.stringify(pending));
    return sessionStorage.getItem(STALE_MODULE_RELOAD_KEY) === pending.url;
  } catch {
    return false;
  }
}

export function takePendingChunkReload(): PendingChunkReload | undefined {
  try {
    const stored = sessionStorage.getItem(PENDING_CHUNK_RELOAD_KEY);
    if (!stored) {
      return;
    }
    sessionStorage.removeItem(PENDING_CHUNK_RELOAD_KEY);
    return JSON.parse(stored) as PendingChunkReload;
  } catch {
    return;
  }
}

export function isStaleModuleError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  if (err instanceof TypeError) {
    return /dynamically imported module|Importing a module script failed/i.test(err.message);
  }
  return /^Unable to preload CSS/i.test(err.message);
}

/**
 * Reloads the page to pick up the current build. Returns false, and does not
 * reload, when this page already reloaded once in this tab or when the
 * attempt can't be recorded (e.g. storage is blocked), so the caller reports
 * the error instead of reloading forever.
 */
export function attemptStaleModuleReload(context: string, err?: unknown): boolean {
  const url = location.href;
  if (!shouldReloadForStaleModule(readLastReloadUrl(), url)) {
    return false;
  }
  const recorded = recordAttempt({
    context,
    at: Date.now(),
    url,
    message: err instanceof Error ? err.message : undefined,
  });
  if (!recorded) {
    return false;
  }
  location.reload();
  return true;
}
